import { defineStore } from 'pinia';
import { getErrorMessage } from '@/database/components/brokerDetail/brokerDetail.setup';
import { ref } from 'vue';
import {
  getV7AiConfig,
  patchV7AiConfig,
} from '@/api-client/ai/queries/ai-config-controller/ai-config-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { AiConfigItem, AiConfigRequest, AiConfigRequestPlatformsItem } from '@/api-client/ai/model';

const AI_CONFIG_CACHE_DURATION = 10 * 60 * 1000; // 10분

export const useApi = () => {
  const saveAiSetting = async ({
    request,
    frameName,
  }: {
    request: AiConfigRequest;
    frameName: string;
  }) => {
    try {
      // 권한 검사는 호출부(aiView.setup)의 Edit 권한 가드가 담당 — 여기서 중복 검사하지 않는다.
      const { data } = await patchV7AiConfig(request, {
        params: {
          frameName,
        },
      });
      return {
        resultMsg: data?.status === 'success' ? 'success' : 'failure',
        error: data?.status === 'failure' ? getErrorMessage(null) : null,
      };
    } catch (e) {
      console.log(e);
      return {
        resultMsg: 'failure',
        error: getErrorMessage(e),
      };
    }
  };

  return { saveAiSetting };
};

const getAiSettingInfo = async (): Promise<AiConfigItem | null> => {
  try {
    const {
      data: { data = [] },
    } = await getV7AiConfig({
      params: {
        frameName: FRAME_NAMES.AI.GET_AI_SETTING,
      },
    });
    return data[0] || {};
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const useAiViewStore = defineStore('aiView', () => {
  const aiInfo = ref<AiConfigItem>({});
  const aiAnomalyDetectionToggleValue = ref(false);
  const selectedAiAnomalyPlatforms = ref<AiConfigRequestPlatformsItem[]>([]);
  const lastFetchedAt = ref<number | null>(null);

  /**
   * @param force true이면 10분 캐시를 무시하고 즉시 API를 호출한다.
   *              저장/취소 등 명시적 갱신이 필요한 경우에만 사용한다.
   */
  const fetchAiInfo = async (force = false) => {
    if (
      !force &&
      lastFetchedAt.value !== null &&
      Date.now() - lastFetchedAt.value < AI_CONFIG_CACHE_DURATION
    ) {
      return;
    }
    const aiSettingInfo = await getAiSettingInfo();
    if (!aiSettingInfo) {
      return;
    }

    aiInfo.value = aiSettingInfo;
    lastFetchedAt.value = Date.now();
    aiAnomalyDetectionToggleValue.value = aiInfo.value?.aiAnomalyMode ?? false;
    selectedAiAnomalyPlatforms.value =
      aiInfo.value.platforms &&
      aiInfo.value.platforms.length === 1 &&
      aiInfo.value.platforms[0] === ''
        ? []
        : ((aiInfo.value.platforms ?? []) as AiConfigRequestPlatformsItem[]);
  };

  return {
    fetchAiInfo,
    aiInfo,
    aiAnomalyDetectionToggleValue,
    selectedAiAnomalyPlatforms,
  };
});
